import React, { useEffect, useState } from "react";
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.js';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry.js';
import * as pdfjsViewer from 'pdfjs-dist/legacy/web/pdf_viewer.js';
import 'pdfjs-dist/legacy/web/pdf_viewer.css';
import PDFThumbnailList from "./PDFThumbnailList";
import styled, { css } from 'styled-components';

import Sidebar from '../icons/Sidebar';
import ArrowUp from '../icons/ArrowUp';
import ArrowDown from '../icons/ArrowDown';
import Plus from '../icons/Plus';
import Minus from '../icons/Minus';
import SearchPage from '../icons/SearchPage';
import Close from '../icons/Close';
import Search from '../icons/Search';

function renderDocument(url, container) {
  const loadingTask = pdfjsLib.getDocument(url);

  return loadingTask.promise.then(function (pdf) {
    // (Optionally) enable hyperlinks within PDF files.
    const pdfLinkService = new pdfjsViewer.PDFLinkService({
      eventBus,
    });

    // (Optionally) enable find controller.
    const pdfFindController = new pdfjsViewer.PDFFindController({
      eventBus,
      linkService: pdfLinkService,
    });

    const pdfViewer = new pdfjsViewer.PDFViewer({
      container,
      eventBus,
      linkService: pdfLinkService,
      findController: pdfFindController,
    });

    pdfViewer._currentScale = 1;

    pdfLinkService.setViewer(pdfViewer);
    pdfLinkService.setDocument(pdf, null);

    pdfViewer.setDocument(pdf);

    return {
      pdf,
      pdfViewer,
    };
  });
}

pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(new Blob([pdfjsWorker]));

const eventBus = new pdfjsViewer.EventBus();

export default function PDFViewer({ url, fileName, isMobile, onLoad = () => {} }) {
  const [totalPageNumber, setTotalPageNumber] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [totalMatches, setTotalMatches] = useState(0);
  const [matchIndex, setMatchIndex] = useState(0);

  const [pdfViewer, setPdfViewer] = useState(null);
  const [thumbnailList, setThumbnailList] = useState([]);

  const [isPreviewSidebarOpen, setIsPreviewSidebarOpen] = useState(false);
  const [isMobileSearchBarOpen, setIsMobileSearchBarOpen] = useState(false);

  const [percentageZoom, setPercentageZoom] = useState(100);
  const [searchEntry, setSearchEntry] = useState('');

  const [pinchZoomEnabled, setPinchZoomEnabled] = useState(false);

  useEffect(() => {
    function updateFindMatchesCount(result) {
      setTotalMatches(result.matchesCount.total);
      setMatchIndex(result.matchesCount.current);
    }

    function updateFindControlState(result) {
      setTotalMatches(result.matchesCount.total);
      setMatchIndex(result.matchesCount.current);
    }

    function pageChanging(result) {
      setCurrentPageNumber(result.pageNumber);
    }

    function scaleChanging(result) {
      setPercentageZoom(Math.round(result.scale * 100));
    }

    function setScaleDependingOnDevice(result) {
      // We compute the correct scale by calculating the distance between the
      // actual page size and the available window size and then applying it
      // using "increaseScale" or "decreaseScale". This logic is similar to
      // the one used by pdf.js when setting "page-fit" (which doesn't actually
      // work correctly in our case).
      const width = (window.innerWidth - 4) / result.source._pages[0].width;
      const height = (window.innerHeight - 132) / result.source._pages[0].height;
      const scale = Math.round(Math.min(width, height) * 10) / 10;

      if (scale > 1) {
        const increment = (scale - 1) * 10;
        pdfViewer.increaseScale(increment);
      } else if (scale < 1) {
        const decrement = (1 - scale) * 10;
        pdfViewer.decreaseScale(decrement);
      }
    }

    eventBus.on('updatefindmatchescount', updateFindMatchesCount);
    eventBus.on('updatefindcontrolstate', updateFindControlState);
    eventBus.on('pagechanging', pageChanging);
    eventBus.on('scalechanging', scaleChanging);
    eventBus.on('pagesloaded', setScaleDependingOnDevice);
    eventBus.on('pagesloaded', onLoad);

    return function () {
      eventBus.off('updatefindmatchescount', updateFindMatchesCount);
      eventBus.off('updatefindmatchescount', updateFindMatchesCount);
      eventBus.off('pagechanging', pageChanging);
      eventBus.off('scalechanging', scaleChanging);
      eventBus.off('pagesloaded', setScaleDependingOnDevice);
      eventBus.off('pagesloaded', onLoad);
    };
  });

  useEffect(() => {
    const container = document.getElementById("viewContainer");

    renderDocument(url, container).then(({ pdf, pdfViewer }) => {
      setPdfViewer(pdfViewer);
      setTotalPageNumber(pdf._pdfInfo.numPages);

      let pageNumber = 1;

      const promises = [];

      while (pageNumber <= pdf._pdfInfo.numPages) {
        const currentPage = pageNumber;

        promises.push(
          new Promise((resolve, reject) => {
            pdf.getPage(currentPage).then(function (page) {
              const viewport = page.getViewport({ scale: 1 });

              // Prepare canvas using PDF page dimensions
              const canvas = document.createElement('canvas');
              canvas.height = viewport.height;
              canvas.width = viewport.width;

              const context = canvas.getContext('2d');

              // Render PDF page into canvas context
              page.render({
                canvasContext: context,
                viewport: viewport,
              }).promise.then(function () {
                return canvas.toBlob(blob => {
                  return resolve(URL.createObjectURL(blob));
                });
              });
            });
          }),
        );

        pageNumber++;
      }

      Promise.all(promises).then(urls => setThumbnailList(urls));
    });
  }, []);

  function searchWord(value) {
    setSearchEntry(value);
    eventBus.dispatch('find', {
      query: value,
      // source: this,
      // type: "",
      // query: null,
      // caseSensitive: false,
      // entireWord: false,
      phraseSearch: true,
      // findPrevious: false,
      // matchDiacritics: false,
    });
  }

  function nextMatch() {
    eventBus.dispatch("find", {
      query: searchEntry,
      type: "again",
      findPrevious: false,
    });
  }

  function previousMatch() {
    eventBus.dispatch("find", {
      query: searchEntry,
      type: "again",
      findPrevious: true,
    });
  }

  function increaseScale() {
    if (pdfViewer === null) {
      console.warn("PDFViewer not ready yet.");
    }

    pdfViewer.increaseScale();
  }

  function decreaseScale() {
    if (pdfViewer === null) {
      console.warn("PDFViewer not ready yet.");
    }

    pdfViewer.decreaseScale();
  }

  function clearSearchEntry() {
    setSearchEntry('');
    setIsMobileSearchBarOpen(false);

    if (isMobile) {
      setIsPreviewSidebarOpen(false);
    }

    eventBus.dispatch('find', { query: '' });
  }

  function openSearchBarMobile() {
    setIsMobileSearchBarOpen(true);
  }

  function setThumbsSwiperClick(result) {
    pdfViewer.linkService.goToPage(result.clickedIndex + 1);
  }

  useEffect(() => {
    document.addEventListener('webviewerloaded', () => {
      if (!pinchZoomEnabled) {
        setPinchZoomEnabled(true);
      }
    });
  });

  useEffect(() => {
    const container = document.getElementById("viewContainer");
    const viewer = document.getElementById("viewer");
    let startX = 0;
    let startY = 0;
    let initialPinchDistance = 0;
    let pinchScale = 1;

    const reset = () => {
      startX = 0;
      startY = 0;
      initialPinchDistance = 0;
      pinchScale = 1;
    };

    function touchStart(event) {
      if (event.touches.length > 1) {
        startX = ((event.touches[0].pageX + event.touches[1].pageX) / 2);
        startY = ((event.touches[0].pageY + event.touches[1].pageY) / 2);
        initialPinchDistance = (Math.hypot((event.touches[1].pageX - event.touches[0].pageX), (event.touches[1].pageY - event.touches[0].pageY)));
      } else {
        initialPinchDistance = 0;
      }
    }

    function touchMove(event) {
      if (initialPinchDistance <= 0 || event.touches.length < 2) {
        return;
      }
      if (event.scale !== 1) {
        event.preventDefault();
      }
      const pinchDistance = Math.hypot((event.touches[1].pageX - event.touches[0].pageX), (event.touches[1].pageY - event.touches[0].pageY));
      const originX = startX + container.scrollLeft;
      const originY = startY + container.scrollTop;
      pinchScale = (pinchDistance / initialPinchDistance);
      viewer.style.transform = `scale(${pinchScale})`;
      viewer.style.transformOrigin = `${originX}px ${originY}px`;
    }

    function touchEnd(event) {
      if (initialPinchDistance <= 0) {
        return;
      }

      viewer.style.transform = 'none';
      viewer.style.transformOrigin = 'unset';

      if (pdfViewer === null) {
        console.warn("PDFViewer not ready yet.");
      }

      setPercentageZoom(Math.round(pdfViewer._currentScale * pinchScale * 100));
      pdfViewer.currentScale = pdfViewer._currentScale * pinchScale;

      const rect = container.getBoundingClientRect();
      const dx = startX - rect.left;
      const dy = startY - rect.top;
      container.scrollLeft += dx * (pinchScale - 1);
      container.scrollTop += dy * (pinchScale - 1);
      reset();
    }

    container.addEventListener('touchstart', touchStart);
    container.addEventListener('touchmove', touchMove, { passive: false });
    container.addEventListener('touchend', touchEnd);

    return function () {
      container.removeEventListener('touchstart', touchStart);
      container.removeEventListener('touchmove', touchMove);
      container.removeEventListener('touchend', touchEnd);
    };
  }, [pinchZoomEnabled, pdfViewer]);

  function toggleSidebar() {
    setIsPreviewSidebarOpen(value => !value);
  }

  return (
    <React.Fragment>
      <Container>
        {!isMobile &&
        <Toolbar>
          <SidebarToolbar isMobile={isMobile}>
            <SidebarButton previewModalOpened={isPreviewSidebarOpen} onClick={() => toggleSidebar()}>
              <Sidebar style={{ color: "black" }} />
            </SidebarButton>
            <FileInfo>
              <FileName isMobile={isMobile}>{fileName} </FileName>
              {totalPageNumber > 0 ? (
                <PageInfo isMobile={isMobile}>Page {currentPageNumber} of {totalPageNumber}</PageInfo>
              ) : (
                <PageInfo isMobile={isMobile}>Page 1</PageInfo>
              )}
            </FileInfo>
          </SidebarToolbar>
          <ZoomFindToolbar>
            <React.Fragment>
              <ZoomButton onClick={() => decreaseScale()}>
                <Minus style={{ color: "black" }} />
              </ZoomButton>
              <PercentageDiv>
                {percentageZoom}%
              </PercentageDiv>
              <ZoomButton onClick={() => increaseScale()}>
                <Plus style={{ color: "black" }} />
              </ZoomButton>
            </React.Fragment>
            <BoxFind isMobile={isMobile}>
              {searchEntry === '' ?
                <FindButton>
                  <Search style={{ color: "black" }} />
                </FindButton>
                :
                <FindButton onClick={() => clearSearchEntry()}>
                  <Close style={{ width: ".75rem", color: "black" }} />
                </FindButton>
              }
              <FindInput
                placeholder="Search"
                isMobile={isMobile}
                onChange={event => searchWord(event.target.value)}
                value={searchEntry}>
              </FindInput>
              {searchEntry !== '' && (
                <MatchInfo>
                  <MatchInfoText>{matchIndex}/{totalMatches}</MatchInfoText>
                  <FindButton disabled={totalMatches === 0} onClick={() => previousMatch()}>
                    <ArrowUp />
                  </FindButton>
                  <FindButton disabled={totalMatches === 0} onClick={() => nextMatch()}>
                    <ArrowDown />
                  </FindButton>
                </MatchInfo>
              )}
            </BoxFind>
          </ZoomFindToolbar>
        </Toolbar>
        }
        {isMobile &&
        <Toolbar>
          {!isMobileSearchBarOpen ?
            <SidebarToolbar isMobile={isMobile}>
              <SidebarButton previewModalOpened={isPreviewSidebarOpen} onClick={() => toggleSidebar()}>
                <Sidebar style={{ color: "black" }} />
              </SidebarButton>
              <FileName isMobile={isMobile}>{fileName}</FileName>
              {totalPageNumber > 0 && (
                <PageInfo isMobile={isMobile}>{currentPageNumber} of {totalPageNumber}</PageInfo>
              )}
              <FindButton isMobile={isMobile} onClick={() => openSearchBarMobile()}>
                <SearchPage />
              </FindButton>
            </SidebarToolbar>
            :
            <BoxFind isMobile={isMobile}>
              <FindButton onClick={() => clearSearchEntry()}>
                <Close style={{ width: "1rem", color: "black" }} />
              </FindButton>
              <FindInput
                id="search-input"
                autoFocus
                placeholder="Search"
                isMobile={isMobile}
                onChange={event => searchWord(event.target.value)}
                value={searchEntry}>
              </FindInput>
              {searchEntry !== '' && (
                <MatchInfo>
                  <MatchInfoText>{matchIndex}/{totalMatches}</MatchInfoText>
                  <FindButton disabled={totalMatches === 0} onClick={() => previousMatch()}>
                    <ArrowUp />
                  </FindButton>
                  <FindButton disabled={totalMatches === 0} onClick={() => nextMatch()}>
                    <ArrowDown />
                  </FindButton>
                </MatchInfo>
              )}
            </BoxFind>
          }
        </Toolbar>
        }

        <div style={{
          flex: 1,
          position: 'relative',
        }}>
          <PDFThumbnailList
            setThumbsSwiperClick={setThumbsSwiperClick}
            isThumbVisible={isPreviewSidebarOpen && (!isMobile || !isMobileSearchBarOpen)}
            currentPageNumber={currentPageNumber}
            isMobile={isMobile}
          >
            {thumbnailList.map((url, index) => (
              <img
                key={url}
                src={url}
                alt={`Page ${index + 1}`}
                style={{
                  width: '100%',
                  borderRadius: '.5rem',
                }}
              />
            ))}
          </PDFThumbnailList>

          <ViewerContainer id="viewContainer" isMobile={isMobile}>
            <PageContainer id="viewer" className="pdfViewer" />
          </ViewerContainer>
        </div>
      </Container>
    </React.Fragment>
  );
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  //background-color: transparent;
  //cursor: pointer;
  background: #dddfe0;
`;

const Toolbar = styled.div`
  z-index: 1; // to make sure the box-shadow goes on top of the PDF pages
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  min-height: 3.5rem;
  background-color: #fff;
  box-shadow: 0 1px rgb(0 0 0 / .1);
`;

const ViewerContainer = styled.div`
  overflow: auto;

  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const SidebarToolbar = styled.div`
  flex: ${({ isMobile }) => isMobile ? 1 : null};
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const FileInfo = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  flex: 1;
  padding-right: .5rem;
  overflow: hidden;
  text-overflow: ellipsis;

  ${props => props.isMobile ? css`
    display: -webkit-box;
    //noinspection CssUnknownProperty
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  ` : css`
    max-width: 20rem;
    white-space: nowrap;
  `};
`;

const PageInfo = styled.div`
  color: ${({ isMobile }) => isMobile ? null : '#abb5c9'};
`;

const ZoomFindToolbar = styled.div`
  flex: 0 1 50%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const PercentageDiv = styled.div`
  margin: 0 1rem;
  font-variant-numeric: tabular-nums;
`;

const BoxFind = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 0 .5rem;
  padding: 0 .5rem;
  width: 100%;
  height: 2rem;
  border: ${({ isMobile }) => isMobile ? null : '.0625rem solid #AAB8D0'};
  border-radius: .25rem;
`;

const FindInput = styled.input`
  flex: 1;
  padding: 0 .5rem;
  border: none;
  text-align: left;

  &:focus {
    outline: none;
  }
`;

const ZoomButton = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #aab8d0;
  border-radius: .25rem;
  background-color: transparent;

  svg {
    width: 1rem;
  }
`;

const FindButton = styled.button`
  flex-shrink: 0;
  z-index: 100;
  padding: .5rem .25rem;
  border: none;
  background-color: transparent;
  margin: 0 ${({ isMobile }) => isMobile ? '1rem' : '0'};
  font-size: 0;
  color: #000;

  &[disabled] {
    opacity: .1;
  }

  svg {
    width: 1rem;
  }
`;

const SidebarButton = styled.div`
  z-index: 100;
  margin: 0 .5rem;
  padding: .5rem;
  border: none;
  background: ${({ previewModalOpened }) => previewModalOpened ? '#ebebeb' : 'transparent'};
  border-radius: 6px;
  font-size: 0;

  svg {
    width: 1.5rem;
  }
;
`;
const MatchInfo = styled.div`
  text-align: right;
`;

const MatchInfoText = styled.span`
  margin-right: .5rem;
`;

const PageContainer = styled.div`
  > .page:first-child {
    margin-top: 1rem;
  }

  > .page {
    margin-bottom: 1rem;
    box-shadow: 0 .125rem .125rem rgb(0 0  0 / .1);
  }
`;


